import type { ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

export type ThemeMode = 'light' | 'dark'

interface ThemeContextValue {
    theme: ThemeMode
    setTheme: (theme: ThemeMode) => void
    toggleTheme: () => void
    forceTheme: (theme: ThemeMode | null) => void
}

interface ThemeProviderProps {
    children: ReactNode
}

const STORAGE_KEY = 'goodunited-theme'

const ThemeContext = createContext<ThemeContextValue>({
    theme: 'light',
    setTheme: () => undefined,
    toggleTheme: () => undefined,
    forceTheme: () => undefined
})

const applyTheme = (theme: ThemeMode) => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    root.style.colorScheme = theme
}

/*
 * Light and dark mode. The saved choice is read once after mount and kept in sync across
 * preview frames. forceTheme pins a mode for one view (the dark templates) without saving it.
 */
export const ThemeProvider = ({ children }: ThemeProviderProps): ReactNode => {
    // Server-safe default. The saved choice is read once after mount.
    const [theme, setThemeState] = useState<ThemeMode>('light')
    const themeRef = useRef<ThemeMode>('light')
    const forcedRef = useRef<ThemeMode | null>(null)

    const sync = useCallback((next: ThemeMode) => {
        themeRef.current = next
        setThemeState(next)
        applyTheme(forcedRef.current ?? next)
    }, [])

    useEffect(() => {
        const saved = window.localStorage.getItem(STORAGE_KEY)
        sync(saved === 'dark' ? 'dark' : 'light')

        const onStorage = (event: StorageEvent) => {
            if (event.key !== STORAGE_KEY) return
            if (event.newValue === 'light' || event.newValue === 'dark') sync(event.newValue)
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [sync])

    const setTheme = useCallback((next: ThemeMode) => {
        sync(next)
        window.localStorage.setItem(STORAGE_KEY, next)
    }, [sync])

    const toggleTheme = useCallback(() => {
        setTheme(themeRef.current === 'dark' ? 'light' : 'dark')
    }, [setTheme])

    const forceTheme = useCallback((next: ThemeMode | null) => {
        forcedRef.current = next
        applyTheme(next ?? themeRef.current)
    }, [])

    const value = useMemo(() => ({ theme, setTheme, toggleTheme, forceTheme }), [theme, setTheme, toggleTheme, forceTheme])

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = (): ThemeContextValue => useContext(ThemeContext)

/* Pins one mode while mounted, for example the dark versions of the templates. */
export const ForceTheme = ({ theme }: { theme: ThemeMode }): null => {
    const { forceTheme } = useTheme()

    useEffect(() => {
        forceTheme(theme)

        return () => forceTheme(null)
    }, [theme, forceTheme])

    return null
}
